import { useEffect, useRef, useState, useCallback } from "react";

// Supabase's autoRefreshToken silently keeps a session alive forever as
// long as the tab stays open — there's no built-in idle expiry. This closes
// that gap client-side: after `idleMinutes` with no mouse/keyboard/scroll
// activity, sign the user out. A warning modal appears `warningSeconds`
// before that happens, dismissed only by the explicit "Stay Signed In"
// button (not by ambient activity) so it can't flicker away from e.g. the
// mouse just resting over the window.

const B = { red: "#A51C30", black: "#141414", white: "#FFFFFF", silver: "#8A8A8A" };
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];

export default function IdleTimeoutGuard({ onTimeout, idleMinutes = 30, warningSeconds = 60 }) {
  const [secondsLeft, setSecondsLeft] = useState(null); // null = warning not showing
  const lastActivityRef = useRef(Date.now());
  const warningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout; // always current without retriggering the effect below

  const idleMs = idleMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;

  const stayActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningRef.current = false;
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    function handleActivity() {
      if (warningRef.current) return; // once warning is showing, only the button counts
      lastActivityRef.current = Date.now();
    }
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= idleMs) {
        clearInterval(interval);
        onTimeoutRef.current();
        return;
      }
      if (elapsed >= idleMs - warningMs) {
        warningRef.current = true;
        setSecondsLeft(Math.ceil((idleMs - elapsed) / 1000));
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      clearInterval(interval);
    };
  }, [idleMs, warningMs]);

  if (secondsLeft === null) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: B.white, borderRadius: 14, padding: 28, width: "100%", maxWidth: 380, textAlign: "center", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 18, fontFamily: "'Playfair Display',serif", color: B.black }}>Still there?</h2>
        <p style={{ margin: "0 0 4px", color: B.black, fontSize: 14 }}>You'll be signed out in</p>
        <div style={{ fontSize: 32, fontWeight: 800, color: B.red, margin: "6px 0 16px", fontVariantNumeric: "tabular-nums" }}>{mm}:{ss}</div>
        <p style={{ margin: "0 0 20px", color: B.silver, fontSize: 12.5 }}>for your security, after a period of inactivity.</p>
        <button onClick={stayActive} style={{ width: "100%", padding: "12px", borderRadius: 9, border: "none", background: B.red, color: B.white, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>
          Stay Signed In
        </button>
      </div>
    </div>
  );
}
